import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';

const typeLabels = {
  win: '🏆 Win',
  setback: '⚡ Setback',
  meeting: '🤝 Meeting',
  idea: '💡 Idea',
  milestone: '🎯 Milestone',
  neutral: '📝 Note',
};

/** Tailwind-safe active styles (dynamic bg-* classes are purged). */
const filterActiveClasses = {
  all: 'bg-gold text-white border-gold',
  win: 'bg-gold text-white border-gold',
  setback: 'bg-amber text-white border-amber',
  meeting: 'bg-teal text-white border-teal',
  idea: 'bg-lavender text-white border-lavender',
  milestone: 'bg-sage text-white border-sage',
  neutral: 'bg-text-primary text-white border-text-primary',
};

const typeAccentVar = {
  win: '--type-win',
  setback: '--type-setback',
  meeting: '--type-meeting',
  idea: '--type-idea',
  milestone: '--type-milestone',
  neutral: '--type-neutral',
};

function entryBody(entry) {
  return entry.body ?? entry.content ?? '';
}

const LogPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchEntries = useCallback(async () => {
    try {
      const data = await apiFetch('/api/entries');
      setEntries(data.entries || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error(error?.message || 'Could not load log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const performDeleteEntry = async () => {
    if (!deleteTarget) return;
    const { id } = deleteTarget;
    await apiFetch(`/api/entries/${id}`, { method: 'DELETE' });
    toast.success('Entry deleted');
    fetchEntries();
  };

  const filteredEntries = entries.filter((entry) => {
    const matchesFilter = filter === 'all' || entry.entry_type === filter;
    const text = `${entryBody(entry)} ${entry.title || ''}`.toLowerCase();
    const matchesSearch = text.includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="log-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Your Log</h1>
            <p className="text-text-secondary">A complete record of your journey</p>
          </div>
          <Link
            to="/log/new"
            className="flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
            data-testid="new-entry-button"
          >
            <Plus size={20} />
            New Entry
          </Link>
        </div>

        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-tertiary" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search entries..."
              className="w-full pl-12 pr-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all border-2 ${
                filter === 'all'
                  ? filterActiveClasses.all
                  : 'bg-bg-surface text-text-secondary border-border hover:border-gold'
              }`}
            >
              All
            </button>
            {Object.entries(typeLabels).map(([type, label]) => (
              <button
                key={type}
                type="button"
                onClick={() => setFilter(type)}
                className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all border-2 ${
                  filter === type
                    ? filterActiveClasses[type]
                    : 'bg-bg-surface text-text-secondary border-border hover:border-gold'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
            <p className="text-text-secondary">Loading entries...</p>
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <div className="w-16 h-16 bg-gold-light rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="text-gold" size={32} />
            </div>
            <h3 className="font-display text-xl font-bold text-text-primary mb-2">No entries yet</h3>
            <p className="text-text-secondary mb-6">Start logging your journey today</p>
            <Link
              to="/log/new"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
            >
              <Plus size={20} />
              Create First Entry
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map((entry) => {
              const accent = typeAccentVar[entry.entry_type] || typeAccentVar.neutral;
              return (
                <div
                  key={entry.id}
                  className="bg-bg-surface rounded-2xl p-6 border-l-4 card-hover"
                  style={{ borderLeftColor: `var(${accent})` }}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex flex-wrap items-center gap-3 min-w-0">
                      <span
                        className="text-sm font-semibold shrink-0"
                        style={{ color: `var(${accent})` }}
                      >
                        {typeLabels[entry.entry_type] || typeLabels.neutral}
                      </span>
                      <span className="font-mono text-xs text-text-tertiary">
                        {new Date(entry.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {entry.energy_level ? (
                        <div className="flex gap-1 mr-2">
                          {[...Array(5)].map((_, i) => (
                            <div
                              key={i}
                              className={`w-2 h-2 rounded-full ${
                                i < entry.energy_level ? 'bg-gold' : 'bg-text-tertiary/20'
                              }`}
                            />
                          ))}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => navigate(`/log/edit/${entry.id}`)}
                        className="p-2 rounded-lg text-text-secondary hover:bg-gold-light hover:text-gold transition-colors"
                        aria-label="Edit entry"
                        data-testid={`edit-entry-${entry.id}`}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteTarget({
                            id: entry.id,
                            preview: entry.title || entryBody(entry).slice(0, 80),
                          })
                        }
                        className="p-2 rounded-lg text-text-secondary hover:bg-error-light hover:text-error transition-colors"
                        aria-label="Delete entry"
                        data-testid={`delete-entry-${entry.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {entry.title ? (
                    <h3 className="font-display text-xl font-bold text-text-primary mb-2">{entry.title}</h3>
                  ) : null}

                  <p className="text-text-secondary leading-relaxed mb-4 line-clamp-3 whitespace-pre-wrap">
                    {entryBody(entry)}
                  </p>

                  <div className="flex gap-2 flex-wrap">
                    {entry.linked_goal_id ? (
                      <span className="px-3 py-1 bg-sage-light text-sage text-xs font-medium rounded-full">
                        🎯 Goal linked
                      </span>
                    ) : null}
                    {entry.linked_person_id ? (
                      <span className="px-3 py-1 bg-teal-light text-teal text-xs font-medium rounded-full">
                        👤 Contact linked
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete this entry?"
        message="This will permanently remove it from your log. You can't undo this."
        detail={deleteTarget?.preview ? String(deleteTarget.preview) : null}
        confirmLabel="Delete entry"
        onConfirm={performDeleteEntry}
      />
    </div>
  );
};

const BookOpen = ({ size, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" className={className}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
    />
  </svg>
);

export default LogPage;
